// src/services/vibeModeration.js — hide, show-less, report, and turning a post's
// comments off. Written against the app's own fields so an action taken in the
// browser means the same thing on the phone.
//
// ══ WHAT THE INVESTIGATION FOUND ═══════════════════════════════════════════
//
// 1. HIDE WAS A NO-OP THAT LOOKED LIKE IT WORKED. The app writes
//    `users/{uid}.hiddenPosts: arrayUnion(postId)` and then — nothing reads it.
//    Not the feed query, not the video feed, not the profile grid. Hiding only
//    dropped the post from the in-memory array, so it came back on the next
//    launch, and it was never going to reach the browser at all. The write was
//    right; the read was missing. `isHidden` below is that read, and the app now
//    filters on it too.
//
// 2. "SHOW LESS LIKE THIS" DID NOT EXIST. Nothing in the app writes or reads any
//    such signal, so there was nothing to sync to — it is new on both sides.
//    Stored as `users/{uid}.lessLike: [authorUid]`, applied as a heavy negative
//    weight in the feed score rather than a hard filter: "show me less of this"
//    is not "never show me this again", and a hard block would quietly turn a
//    soft preference into an unblockable mute the user can't see or undo.
//
// 3. ONE PERSON IS ONE REPORT, HOWEVER MANY REASONS THEY GIVE.
//    Someone may legitimately feel a post is both hateful AND a scam, so filing
//    a second reason is allowed and the extra reason is kept — it is real signal
//    for whoever reviews it. What it must NOT do is count twice.
//
//    🟥 IT USED TO. `reportCount` was bumped per report ENTRY, and the ban
//       ladder counts off that, so one determined person picking all four
//       headings contributed four reports towards someone else's ban — 40% of
//       the way there on their own. A ban ladder that one account can climb for
//       you is not a community signal, it is a weapon. The count and the strike
//       now move only on a person's FIRST report of a post.

import {
  doc, updateDoc, arrayUnion, arrayRemove, deleteDoc,
} from 'firebase/firestore';
import { db, auth } from '../config/firebase';

const BACKEND = process.env.REACT_APP_BACKEND_URL || 'https://signaling-server-prod-397203671414.us-central1.run.app';

/**
 * 🟥 "SHOW LESS LIKE THIS" USED TO GO NOWHERE ANYONE COULD READ. Both of the
 *    functions below write to the VIEWER'S own user document — `hiddenPosts` and
 *    `lessLike` — which is exactly right for a personal preference and useless
 *    as a signal: answering "which posts are people hiding" would have meant
 *    reading every user document in the product. So nobody ever asked.
 *
 *    This tells the server, which keeps the count on the post itself. It is
 *    fire-and-forget on purpose:
 *
 *  🟢 THE PREFERENCE IS SAVED WHETHER OR NOT THIS SUCCEEDS. The Firestore write
 *     above is what actually hides the post; this is telemetry about it. Making
 *     the hide wait on a second network call — or fail with it — would trade a
 *     working feature for a statistic.
 *
 *  🟢 AND THE SERVER COUNTS, NOT THE CLIENT. Letting a browser increment a
 *     counter on somebody else's post needs a Firestore rule granting every user
 *     a write on every post, which cannot be written narrowly enough to be safe.
 */
function signalHide(body) {
  (async () => {
    try {
      const u = auth.currentUser;
      if (!u) return;
      await fetch(`${BACKEND}/api/signals/hide`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${await u.getIdToken()}` },
        body: JSON.stringify(body),
        keepalive: true,     // survives the navigation a hide is often followed by
      });
    } catch { /* a statistic is not worth a retry */ }
  })();
}

/** The four headings the app offers, in the app's order. */
export const REPORT_REASONS = [
  { id: 'nudity', label: 'Nude or sexual content' },
  { id: 'violence', label: 'Violence or gore' },
  { id: 'hate', label: 'Hatred or bullying' },
  { id: 'spam', label: 'Spam or scam' },
];

/* ── hide ─────────────────────────────────────────────────────────────────── */

export const isHidden = (post, hiddenIds) => !!post?.id && !!hiddenIds?.has?.(post.id);

export function hidePost(uid, postId) {
  signalHide({ postId, kind: 'post' });
  return updateDoc(doc(db, 'users', uid), { hiddenPosts: arrayUnion(postId) });
}

/** Undo — there is no UI for it yet, but a hide you cannot reverse is a trap. */
export function unhidePost(uid, postId) {
  return updateDoc(doc(db, 'users', uid), { hiddenPosts: arrayRemove(postId) });
}

/* ── show less like this ──────────────────────────────────────────────────── */

export function showLessLike(uid, authorUid) {
  if (!authorUid || authorUid === uid) return Promise.resolve();
  signalHide({ authorUid, kind: 'author' });
  return updateDoc(doc(db, 'users', uid), { lessLike: arrayUnion(authorUid) });
}

export function undoShowLess(uid, authorUid) {
  return updateDoc(doc(db, 'users', uid), { lessLike: arrayRemove(authorUid) });
}

/* ── report ───────────────────────────────────────────────────────────────── */

export class AlreadyReported extends Error {}

/* ══ THE BAN LADDER ════════════════════════════════════════════════════════
   🟥 WHAT THE APP ACTUALLY DID, BEFORE THIS. Two findings worth stating plainly:

     1. STRIKES EARNED FROM REPORTS COULD NEVER BAN ANYONE. The 10/20/30 →
        7d/30d/permanent escalation lived INSIDE the create-post flow, and only
        ran when the AI moderation check refused a post. A post hitting 100
        reports incremented `strikes` and stopped there — so an account could
        collect strike after strike from the community and never once be stopped
        from posting. The ladder existed; nothing climbed it.
     2. THE THRESHOLD WAS 100 REPORTS ON A SINGLE POST for one strike. In
        practice that is unreachable, so reports were decorative.

   THE POLICY NOW (your numbers): reports count against the ACCOUNT, not the
   post. Every REPORTS_PER_BAN upheld reports the account is stopped from
   posting, and each ban is longer than the last until it is permanent.

   The durations are a plain table on purpose — this is a policy, and a policy
   should be one obvious place you can change, not arithmetic buried in a
   handler. */
export const REPORTS_PER_BAN = 10;

/** Ban #1 → 3 days, #2 → a week, #3 → a month, #4 and after → permanent. */
export const BAN_LADDER_DAYS = [3, 7, 30, null];   // null = forever

export function banForOffence(offenceNumber) {
  const i = Math.max(0, offenceNumber - 1);
  const days = i < BAN_LADDER_DAYS.length ? BAN_LADDER_DAYS[i] : null;
  const at = new Date();
  if (days == null) {
    // the app's own convention for "permanent" — a date nothing will outlive
    at.setFullYear(2100);
    return { until: at, days: null, permanent: true };
  }
  at.setDate(at.getDate() + days);
  return { until: at, days, permanent: false };
}

/** Is this account barred from posting right now? */
export function banState(userDoc) {
  const raw = userDoc?.banExpiresAt;
  const until = raw?.toDate ? raw.toDate() : (raw ? new Date(raw) : null);
  if (!until || Number.isNaN(until.getTime()) || until <= new Date()) {
    return { banned: false, until: null, permanent: false };
  }
  return { banned: true, until, permanent: until.getFullYear() >= 2100 };
}

/**
 * File a report — on the server (reportRoutes.js).
 *
 * 🟥 THIS WAS A TRANSACTION IN THE BROWSER that wrote the post's report count
 *    (any value — and the rules then let the same browser delete the post at 99)
 *    and the AUTHOR's strikes and ban, which the rules refuse, so most reports
 *    failed outright. The same policy now runs with the server's authority.
 *    `reason` is the id ('nudity' · 'violence' · 'hate' · 'spam'), never a label.
 */
export async function reportPost({ post, reason }) {
  const u = auth.currentUser;
  if (!u) throw new Error('signed_out');
  const res = await fetch(`${BACKEND}/api/vibes/report`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${await u.getIdToken()}` },
    body: JSON.stringify({ postId: post.id, reason }),
  });
  let j = null;
  try { j = await res.json(); } catch { j = null; }
  if (res.ok && j && j.ok) return j;
  if (j && j.error === 'already_reported') throw new AlreadyReported();
  throw new Error((j && j.error) || `http_${res.status}`);
}

/* ── my own post ──────────────────────────────────────────────────────────── */

export function setCommentsEnabled(postId, enabled) {
  return updateDoc(doc(db, 'posts', postId), { commentsEnabled: !!enabled });
}

export function deletePost(postId) {
  return deleteDoc(doc(db, 'posts', postId));
}
